<script lang="ts">
  import { onMount } from "svelte";
  import type { Snippet } from "svelte";
  import type { MaximizablePanelId } from "./layout";
  import { fittedTargetHeight } from "./focusLayout.ts";

  interface Props {
    panelId: MaximizablePanelId;
    panelClass: string;
    cellStyle: string;
    maximized: boolean;
    hidden: boolean;
    ontoggle: () => void;
    fitSelector?: string | null;
    onfit?: ((height: number) => void) | null;
    children?: Snippet;
  }

  let {
    panelId, panelClass, cellStyle, maximized, hidden, ontoggle,
    fitSelector = null, onfit = null, children,
  }: Props = $props();
  let frame: HTMLDivElement;
  let fitFrame = 0;

  function measureFit() {
    if (!maximized || !fitSelector || !onfit || !frame) return;
    const target = frame.querySelector<HTMLElement>(fitSelector);
    const panel = target?.closest<HTMLElement>("section.card");
    if (!target || !panel) return;
    const panelStyle = getComputedStyle(panel);
    const panelBorderHeight = Number.parseFloat(panelStyle.borderTopWidth) +
      Number.parseFloat(panelStyle.borderBottomWidth);
    onfit(fittedTargetHeight(
      frame.clientHeight,
      panel.scrollHeight,
      target.clientHeight,
      panelBorderHeight,
    ));
  }

  function scheduleFit() {
    cancelAnimationFrame(fitFrame);
    fitFrame = requestAnimationFrame(measureFit);
  }

  onMount(() => {
    const observer = new ResizeObserver(scheduleFit);
    observer.observe(frame);
    const panel = fitSelector ? frame.querySelector<HTMLElement>(fitSelector)?.closest<HTMLElement>("section.card") : null;
    if (panel) observer.observe(panel);
    window.addEventListener("resize", scheduleFit);
    scheduleFit();
    return () => {
      cancelAnimationFrame(fitFrame);
      observer.disconnect();
      window.removeEventListener("resize", scheduleFit);
    };
  });

  $effect(() => {
    void maximized;
    void fitSelector;
    scheduleFit();
  });
</script>

<div
  class={`cell maximize-frame ${panelClass}`}
  class:maximized
  class:hidden
  style={cellStyle}
  bind:this={frame}
  data-panel-mount={panelId}>
  <button
    class="maximize-control"
    type="button"
    data-maximize-panel={panelId}
    aria-label={`${maximized ? "Restore" : "Maximize"} ${panelId}`}
    aria-pressed={maximized}
    title={maximized ? "Restore panel" : "Maximize panel"}
    onclick={ontoggle}>⛶</button>
  {@render children?.()}
</div>

<style>
  .maximize-frame {
    min-width: 0;
    position: relative;
  }
  /* ORDER_HUD_FACE_LEGIBILITY_1 R3: the control's box (8px inset + 28px + 8px air = 44px) belongs to the frame, so the
     mounted panel's header never draws text under it (at 390 it hid CALL TILT's value and the INDEX rung tabs). A
     literal, not a custom property: LAW 8 rule 3 admits var() only for tokens.css, which is outside this order's fence. */
  .maximize-frame > :global(section.card > header),
  .maximize-frame > :global(.maximized-chart-host > section.card > header),
  .maximize-frame > :global(section.card > .hd) { padding-inline-end: 44px; }
  .maximize-frame.hidden { display: none; }
  .maximize-control {
    position: absolute;
    inset-block-start: 8px;
    inset-inline-end: 8px;
    z-index: 2;
    display: grid;
    place-items: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid var(--line);
    border-radius: var(--radius-card);
    background: var(--surface);
    color: var(--muted);
    font: inherit;
    cursor: pointer;
  }
  .maximize-control:hover { color: var(--ink); }
  .maximize-control:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
</style>
